import { Trans, useLingui } from "@lingui/react/macro";
import type { Routine } from "@rakazo/contracts";
import { Switch } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { cronLabels } from "../lib/cron";
import { useSpaceQueries } from "../lib/query";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";
import { routineTriggerSummary } from "../pages/RoutineEditor";
import { RoutineRunHistory } from "../pages/RoutineRunHistory";

const NO_ROUTINES: Routine[] = [];

function CopyRoutineRow({
  routine,
  pending,
  expanded,
  onToggleActive,
  onToggleHistory,
}: {
  routine: Routine;
  pending: boolean;
  expanded: boolean;
  onToggleActive: (active: boolean) => void;
  onToggleHistory: () => void;
}) {
  const { i18n, t } = useLingui();
  const name = routine.name;
  return (
    <li className="py-2.5" data-testid="copy-routine">
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggleHistory}
          className="min-w-0 flex-1 text-start"
        >
          <span className="block truncate text-[14.5px] font-medium text-foreground" dir="auto">
            {routine.name}
          </span>
          <span className="block truncate text-[12.5px] text-muted-foreground/80">
            {routineTriggerSummary(routine, cronLabels(i18n.locale))}
          </span>
        </button>
        <Switch
          aria-label={routine.active ? t`Pause ${name}` : t`Resume ${name}`}
          checked={routine.active}
          disabled={pending}
          onCheckedChange={onToggleActive}
        />
      </div>
      {expanded ? <RoutineRunHistory routineId={routine.id} /> : null}
    </li>
  );
}

/**
 * The routines of a copy: pause or resume each one and read its run history. IT writes them; the
 * only change the server accepts from an employee is the active switch.
 */
export function CopyRoutines({ botId }: { botId: string }) {
  const { t } = useLingui();
  const queries = useSpaceQueries();
  const queryClient = useQueryClient();
  const list = useQuery(queries.routines.list.queryOptions({ input: { botId } }));
  const routines = list.data ?? NO_ROUTINES;
  const [pending, setPending] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function setActive(routine: Routine, active: boolean) {
    setPending(routine.id);
    setError(null);
    try {
      await rpc.routines.update({ routineId: routine.id, active });
      await queryClient.invalidateQueries({
        queryKey: queries.routines.list.queryKey({ input: { botId } }),
      });
    } catch (err) {
      setError(errorText(err, t`Could not update the routine`));
    } finally {
      setPending(null);
    }
  }

  return (
    <section className="mt-6" data-testid="copy-routines">
      <h3 className="mb-2 text-xs font-medium text-muted-foreground">
        <Trans>Routines</Trans>
      </h3>
      {list.isError ? (
        <p className="text-[13px] text-destructive">
          <Trans>Could not load</Trans>
        </p>
      ) : null}
      {!list.isPending && !list.isError && routines.length === 0 ? (
        <p className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
          <Trans>No routines yet</Trans>
        </p>
      ) : null}
      {routines.length > 0 ? (
        <ul className="divide-y divide-border rounded-xl bg-muted/50 px-4">
          {routines.map((routine) => (
            <CopyRoutineRow
              key={routine.id}
              routine={routine}
              pending={pending === routine.id}
              expanded={expanded === routine.id}
              onToggleActive={(active) => void setActive(routine, active)}
              onToggleHistory={() => setExpanded(expanded === routine.id ? null : routine.id)}
            />
          ))}
        </ul>
      ) : null}
      {error ? <p className="mt-2 text-[13px] text-destructive">{error}</p> : null}
    </section>
  );
}
